import { z } from "zod";
/**
 * Customer texts Vekha sends by itself, without the model. The owner edits
 * them in one place; a calendar event may override any of them. Stored rows
 * hold only overrides, so an empty field keeps the inherited text.
 */
export const AUTO_MESSAGE_TIME_PLACEHOLDER = "{{appointment_time}}";
const Text = z.string().trim().min(1).max(4_000);
export const AutoMessageReminderSchema = z.object({
    // Same bounds as the schedule binding reminders these texts replace.
    offsetMinutesBefore: z.number().int().min(0).max(43_200),
    message: Text,
}).strict();
const Reminders = z.array(AutoMessageReminderSchema).max(10).superRefine((reminders, context) => {
    const offsets = new Set();
    reminders.forEach((reminder, index) => {
        if (offsets.has(reminder.offsetMinutesBefore)) {
            context.addIssue({ code: "custom", path: [index, "offsetMinutesBefore"], message: "Два напоминания на одно и то же время." });
        }
        offsets.add(reminder.offsetMinutesBefore);
    });
});
/** One catalogue for validation, defaults, editing and the bot funnel inventory. */
export const AUTO_MESSAGE_REGISTRY = Object.freeze({
    selfBookingPreparation: {
        kind: "text", schema: Text, title: "Памятка после самозаписи", rows: 10,
        hint: "Уходит через 2 минуты после самозаписи, когда найдены сделка и канал клиента.", usesTime: true, eventScope: false,
        defaultText: `Здравствуйте! 😊

Вы записаны на {{appointment_time}} по МСК.

Консультация будет проходить по ссылке, которую мы пришлём в назначенное время, ничего скачивать не нужно.
Она длится около 30–40 минут, желательно выделить это время без спешки, чтобы всё прошло спокойно и с пользой.

Лучше быть в обстановке, где вам будет комфортно сосредоточиться и включиться в разговор, не в дороге и не за рулём 🙌🏻

❗️Обратите внимание, что это именно консультация, а не сеанс гипноза. На встрече мы подробно обсудим ваш запрос, определим подходящие варианты работы и ответим на любые вопросы.

Перед встречей пришлём напоминание. Пожалуйста, оставайтесь на связи 🙏

А пока можно ознакомиться с телеграм-каналом Дмитрия: https://telegram.me/+krVuAadzlDI5YjAy
Сможете ещё до консультации узнать для себя много нового 😊

Хорошего дня 🫶🏽`,
        funnel: { entry: "booking", id: "vekha:memo", title: "Памятка после записи", trigger: "записался на консультацию в календаре",
            delay: "через 2 минуты после записи", stats: "memo", statsNote: null },
    },
    selfBookingConfirmation: {
        kind: "text", schema: Text, title: "Подтверждение самозаписи", rows: 3,
        hint: "Отдельное сообщение после памятки. Просим подтвердить выбранное время перед передачей консультанту.", usesTime: true, eventScope: true,
        defaultText: "Подтвердите, пожалуйста, запись на {{appointment_time}} по Москве. Ответьте «Да, приду». Если время не подходит, напишите 🙂",
        funnel: { entry: "booking", id: "vekha:confirmation", title: "Подтверждение самозаписи", trigger: "памятка доставлена, запись ещё не подтверждена, сделку ведёт Яна на Павле",
            delay: "следом за памяткой, один раз для выбранного времени", stats: "confirmation", statsNote: null },
    },
    reminders: {
        kind: "reminders", schema: Reminders, title: "Напоминания перед встречей", eventScope: true,
        funnel: { entry: "booking", id: "vekha:reminder", trigger: "есть запись на консультацию" },
    },
    lateReminder: {
        kind: "text", schema: Text, title: "Напоминание после сбоя", rows: 3,
        hint: "Одно сообщение, если напоминание за 2 часа не успело уйти вовремя.", usesTime: true, eventScope: true,
        defaultText: "Напоминаю: Ваша консультация {{appointment_time}} по Москве. Всё в силе? Ответьте, пожалуйста, «да».",
        funnel: { entry: "booking", id: "vekha:late-reminder", title: "Напоминание после сбоя", trigger: "напоминание за 2 ч не ушло вовремя, а до встречи больше 15 минут",
            delay: "один раз, вместо пропущенного", stats: "none", statsNote: "считается вместе с напоминанием за 2 ч" },
    },
    abandonmentFirstContact: {
        kind: "text", schema: Text, title: "Открыл календарь и не записался", rows: 5,
        hint: "Первое сообщение новому человеку через 10 минут. Тем, кто уже переписывался, отвечает Яна.", usesTime: false, eventScope: false,
        defaultText: "Здравствуйте! Меня зовут Яна, я из команды Дмитрия Кривиц 😊\n\nВы открывали календарь консультаций в Практикуме, но пока не выбрали дату и время.\n\nДавайте я помогу Вам записаться. В какой день и в какое время Вам удобно?",
        funnel: { entry: "webinar_application", id: "vekha:first-contact", title: "Первое сообщение Яны", trigger: "открыл календарь консультации и не выбрал время (новый диалог)",
            delay: "через 10 минут, если не записался", stats: "firstContact", statsNote: "считаем первые сообщения, подготовленные шаблоном; при прежней переписке Яна пишет сама" },
    },
    careFollowup: {
        kind: "text", schema: z.string().trim().min(1).max(500), title: "Перенос только через заботу", rows: 2,
        hint: "Ответ в мини-приложении, когда клиенту нельзя самому выбрать новое время.", usesTime: false, eventScope: false,
        defaultText: "Чтобы выбрать новое время, напишите в отдел заботы, сотрудник поможет с записью.",
        funnel: { entry: "booking", id: "vekha:care-followup", title: "Перенос через отдел заботы", trigger: "хочет перенести запись в Mini App",
            delay: "сразу, ответом в Mini App", stats: "none", statsNote: "ответ на экране Mini App, отправки не считаются" },
    },
});
export const AUTO_MESSAGE_KEYS = Object.freeze(Object.keys(AUTO_MESSAGE_REGISTRY));
export const AUTO_MESSAGE_TEXT_KEYS = Object.freeze(AUTO_MESSAGE_KEYS.filter((key) => AUTO_MESSAGE_REGISTRY[key].kind === "text"));
export const AUTO_MESSAGE_TEXT_FIELDS = Object.freeze(AUTO_MESSAGE_TEXT_KEYS.map(key => ({ key, ...AUTO_MESSAGE_REGISTRY[key] })));
const shape = Object.fromEntries(AUTO_MESSAGE_KEYS.map(key => [key, AUTO_MESSAGE_REGISTRY[key].schema]));
export const AutoMessageTextsSchema = z.object(shape).strict();
/** Keep the existing binding's reminders as the default until the owner edits them. */
export function builtInAutoMessages(bindingReminders) {
    return AutoMessageTextsSchema.parse(Object.fromEntries(AUTO_MESSAGE_KEYS.map(key => {
        const definition = AUTO_MESSAGE_REGISTRY[key];
        return [key, definition.kind === "text" ? definition.defaultText : bindingReminders.map(reminder => ({ ...reminder }))];
    })));
}
export const AutoMessageOverridesSchema = AutoMessageTextsSchema.partial().strict();
export const AutoMessagesStateSchema = z.object({
    eventId: z.string().uuid().nullable(),
    eventTitle: z.string().nullable(),
    version: z.number().int().nonnegative(),
    overrides: AutoMessageOverridesSchema,
    /** What applies below this level: global set for an event, built-in texts for the global set. */
    inherited: AutoMessageTextsSchema,
    effective: AutoMessageTextsSchema,
    updatedAt: z.string().datetime({ offset: true }).nullable(),
}).strict();
export const AutoMessagesMutationSchema = z.object({
    eventId: z.string().uuid().nullable(),
    idempotencyKey: z.string().min(16).max(160),
    expectedVersion: z.number().int().nonnegative(),
    overrides: AutoMessageOverridesSchema,
}).strict();
/** Later layers win field by field; reminders are replaced as a whole list. */
export function resolveAutoMessages(base, ...layers) {
    const result = { ...base, reminders: [...base.reminders] };
    for (const layer of layers) {
        for (const key of AUTO_MESSAGE_KEYS) {
            const value = layer[key];
            if (value !== undefined)
                result[key] = value;
        }
    }
    return AutoMessageTextsSchema.parse(result);
}
export function renderAppointmentTime(template, label) {
    return template.replaceAll(AUTO_MESSAGE_TIME_PLACEHOLDER, label).replaceAll("{{appointment_time_msk}}", label);
}
//# sourceMappingURL=auto-messages.js.map